import * as React from 'react';
import {Helmet} from 'react-helmet';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {FilterObject} from 'nutshell-core/types';

import {headTitle} from 'shells/document/head-title';

import type {
    WfFormFragment as Form,
    FormUpdateInput,
    FormFieldUpdateInput,
} from 'nutshell-graphql-types';

import type {ReportURLParams} from '../../reports/report-types';

import {WfFormQuery} from '../graphql/wf-form-query';

import {ConnectedFormsPageContent} from './connected-forms-page-content';
import {useFieldUpdate} from './use-field-update';
import {useWfFormUpdate} from './use-wf-form-update';

type OwnProps = ContextRouter & {
    id: string;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    form: Form | null | undefined;
    error?: string | null | undefined;
    isLoading: boolean;
    onUpdateForm: (arg1: FormUpdateInput) => Promise<any>;
    onUpdateField: (arg1: FormFieldUpdateInput) => Promise<any>;
    refetchForm: () => Promise<any>;
};

function ConnectedFormsPageComponent(props: Props) {
    const activeTab = props.match.params.tab;

    const setSelectedTab = (newTab: string) => {
        const newPath = generatePath(props.match.path, {
            formId: props.match.params.formId,
            tab: newTab,
        });

        props.history.push({
            pathname: newPath,
            search: props.history.location.search,
        });
    };

    return (
        <>
            <Helmet>
                <title>
                    {headTitle(
                        props.form && props.form.name.length ? `Form • ${props.form.name}` : 'Form'
                    )}
                </title>
            </Helmet>
            <ConnectedFormsPageContent
                {...props}
                activeTab={activeTab || 'main'}
                onSetSelectedTab={(newTab: string) => setSelectedTab(newTab)}
                activeFilters={props.activeFilters}
                onParamChange={props.onParamChange}
                onParamPush={props.onParamPush}
                urlParams={props.urlParams}
                onRemoveAllFilters={props.onRemoveAllFilters}
                onUpdateForm={props.onUpdateForm}
                refetchForm={props.refetchForm}
                onUpdateField={props.onUpdateField}
            />
        </>
    );
}

export function ConnectedFormsPage(props: OwnProps) {
    const updateWfForm = useWfFormUpdate();
    const updateField = useFieldUpdate();

    return (
        <WfFormQuery formId={props.id}>
            {({form, isLoading, error, refetch: refetchForm}) => (
                <ConnectedFormsPageComponent
                    {...props}
                    form={form}
                    error={error}
                    isLoading={isLoading}
                    refetchForm={refetchForm}
                    onUpdateForm={(input: FormUpdateInput) => {
                        return updateWfForm(input).then(
                            (mutationResults) =>
                                mutationResults &&
                                mutationResults.data &&
                                mutationResults.data.formUpdate &&
                                mutationResults.data.formUpdate.form
                        );
                    }}
                    onUpdateField={(input: FormFieldUpdateInput) => {
                        return (
                            updateField(input)
                                .then(
                                    (mutationResults) =>
                                        mutationResults &&
                                        mutationResults.data &&
                                        mutationResults.data.formFieldUpdate &&
                                        mutationResults.data.formFieldUpdate.field
                                )
                                // Keeps the create rules in sync with mapped fields
                                .then(refetchForm)
                        );
                    }}
                />
            )}
        </WfFormQuery>
    );
}
